import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import axiosInstance from 'ns_libs/nsAxios';
import API_ENDPOINTS from 'services/urls';
import { IExtractionResult } from './useExtractProFormaValues';

type TaskStatusValue = 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';

export interface IExtractionStatusResponse {
    taskId: string;
    status: TaskStatusValue;
    message: string;
    result?: IExtractionResult;
    currentStep?: number;
    error?: string;
}

const MAX_POLLING_DURATION_MS = 15 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;

const fetchExtractionStatus = async (
    taskId: string,
    organizationId: string,
): Promise<IExtractionStatusResponse> => {
    const response = await axiosInstance.get(API_ENDPOINTS.TASKS.TASK_STATUS(taskId), {
        params: { organizationId },
    });
    return response.data;
};

interface IOptions {
    taskId?: string | null;
    organizationId: string;
    onTimeout?: () => void;
}

const useProFormaExtractionStatus = ({ taskId, organizationId, onTimeout }: IOptions) => {
    const enabled = !!taskId;
    const startedAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (enabled && !startedAtRef.current) {
            startedAtRef.current = Date.now();
        }
        if (!enabled) {
            startedAtRef.current = null;
        }
    }, [enabled, taskId]);

    return useQuery({
        queryKey: ['proFormaExtractionStatus', taskId, organizationId],
        queryFn: () => fetchExtractionStatus(taskId!, organizationId),
        enabled,
        refetchInterval: query => {
            if (query.state.status === 'error') return false;
            const { data } = query.state;
            if (!data || data.status === 'SUCCESS' || data.status === 'FAILURE') return false;
            if (startedAtRef.current && Date.now() - startedAtRef.current > MAX_POLLING_DURATION_MS) {
                console.error('The request timed out');
                onTimeout?.();
                return false;
            }
            return POLL_INTERVAL_MS;
        },
        retry: 3,
        retryDelay: 1000,
    });
};

export default useProFormaExtractionStatus;
